import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  costFormater,
  FieldWrapper,
  Label,
  Option,
  RadioButton,
  SelectDropdown,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { IReturnedCheckDto } from '@/dtos/models';

import { CheckDecision, CheckProcessingType } from '../returnedChecks.types';

interface Props {
  check: IReturnedCheckDto;
  decision?: CheckDecision;
  paymentMethodOptions: Option[];
  onlyCheckLeft: boolean;
  onSelect: (lockboxId: number, type: CheckProcessingType) => void;
  onSelectPaymentMethod: (lockboxId: number, paymentMethodId: number) => void;
}

const ReturnedParticipantCheckCard: React.FC<Props> = ({
  check,
  decision,
  paymentMethodOptions,
  onlyCheckLeft,
  onSelect,
  onSelectPaymentMethod,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const lockboxId = check.lockbox_id as number;
  const testIdBase = `returned-check_participant_${lockboxId}`;
  const isReissue = decision?.type === CheckProcessingType.REISSUE;

  return (
    <Box
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="canvas"
      round="container1Round"
      border={{ color: 'border2', size: '1px' }}
      gap="spacing16"
      data-testid={testIdBase}
    >
      <Text size="large" color="textBody" data-testid={`${testIdBase}_title`}>
        {t('Returned Participant Check')} ({costFormater(check.check_amount ?? 0, true)})
      </Text>

      <FieldWrapper direction={isMobile ? 'column' : 'row'} align="start">
        <Label name={`${testIdBase}_action`} isNarrow={!isMobile} showRequireIcon>
          {t('Take action')}
        </Label>
        <Box gap="spacing12" fill="horizontal">
          <RadioButton
            data-testid={`${testIdBase}_void-action`}
            name={`${testIdBase}_action`}
            checked={decision?.type === CheckProcessingType.VOID}
            onChange={() => onSelect(lockboxId, CheckProcessingType.VOID)}
            label={(
              <Box height="24px" align="center">
                <Text color="textBody" margin={{ vertical: 'auto' }}>
                  {t('Void')}
                </Text>
              </Box>
            )}
          />
          <RadioButton
            data-testid={`${testIdBase}_reissue-action`}
            name={`${testIdBase}_action`}
            checked={isReissue}
            onChange={() => onSelect(lockboxId, CheckProcessingType.REISSUE)}
            label={(
              <Box height="24px" align="center">
                <Text color="textBody" margin={{ vertical: 'auto' }}>
                  {t('Reissue')}
                </Text>
              </Box>
            )}
          />

          {isReissue && (
            onlyCheckLeft ? (
              <Box
                pad="spacing16"
                background="warningContainer"
                border={{ color: 'warningBorder', size: '1px' }}
                round="fieldRound"
                data-testid={`${testIdBase}_only-check-message`}
              >
                <Text color="textBody">
                  {t('Your checks are processed but your address still needs to be updated by your administrator.')}
                </Text>
              </Box>
            ) : (
              <Box width={isMobile ? '100%' : '280px'}>
                <SelectDropdown
                  id={`${testIdBase}_payment-method`}
                  testId={`${testIdBase}_payment-method`}
                  name={t('Payment method')}
                  prefix={t('Payment method')}
                  value={decision?.paymentMethodId ? `${decision.paymentMethodId}` : undefined}
                  onChange={(value) => onSelectPaymentMethod(lockboxId, Number(value))}
                  options={paymentMethodOptions}
                  singleMode
                />
              </Box>
            )
          )}
        </Box>
      </FieldWrapper>
    </Box>
  );
};

export default ReturnedParticipantCheckCard;
